// ✅ src/pages/VehicleV2/FileManagerInsuranceDoc.jsx
// React version of: FileManagerInsurancedoc.aspx
// ✅ NOW uses docId (NOT insuranceVehicleId)
// - Reads docId from navigate state OR querystring OR sessionStorage
// - Calls backend:
//     GET  /api/fileManagerInsuranceDoc/list?docId=123
//     POST /api/fileManagerInsuranceDoc/remove  body: { ids:[...], docId:123 }
// - View opens: /VehicleV2/InsuranceDocument/<filename>

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaTrash, FaEye } from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/fileManagerInsuranceDoc";

/* ---------- helpers ---------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

export default function FileManagerInsuranceDoc() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  const { skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ docId can come from:
  // 1) navigate("/vehicle/FileManagerInsuranceDocs", { state: { docId } })
  // 2) /vehicle/FileManagerInsuranceDocs?docId=123
  // 3) sessionStorage.setItem("InsuranceDocId","123") (optional fallback)
  const docId = useMemo(() => {
    const fromState = toInt(state?.docId, 0);
    if (fromState) return fromState;

    const fromQS = toInt(searchParams.get("docId"), 0);
    if (fromQS) return fromQS;

    const fromSession = toInt(sessionStorage.getItem("InsuranceDocId"), 0);
    if (fromSession) return fromSession;

    return 0;
  }, [state, searchParams]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({}); // { fld_Insurancedocumentid: true/false }

  const allChecked = useMemo(() => {
    if (!rows.length) return false;
    return rows.every((r) => selected[r.fld_Insurancedocumentid]);
  }, [rows, selected]);

  const anyChecked = useMemo(() => Object.values(selected).some(Boolean), [selected]);

  const fetchDocs = async () => {
    if (!docId) {
      toast.error("Document ID not found (docId)");
      setRows([]);
      setSelected({});
      return;
    }

    setLoading(true);
    try {
      // ✅ NOW: list by docId
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: { docId },
      });

      if (data?.success === false) throw new Error(data?.message || "Failed to load");

      const list = Array.isArray(data?.rows) ? data.rows : [];
      setRows(list);

      const nextSel = {};
      for (const r of list) nextSel[r.fld_Insurancedocumentid] = false;
      setSelected(nextSel);

      if (!list.length) toast.info("No Records Found");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Error loading documents");
      setRows([]);
      setSelected({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const toggleRow = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = () => {
    if (!rows.length) return;
    const toValue = !allChecked;
    const next = {};
    for (const r of rows) next[r.fld_Insurancedocumentid] = toValue;
    setSelected(next);
  };

  // ✅ ASPX View: "../VehicleV2/InsuranceDocument/" + filename
  const handleView = (filename) => {
    if (!filename) return;
    const url = `/VehicleV2/InsuranceDocument/${encodeURIComponent(filename)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleRemove = async () => {
    if (!anyChecked) return toast.info("Select at least one document");

    const ids = rows
      .filter((r) => selected[r.fld_Insurancedocumentid])
      .map((r) => Number(r.fld_Insurancedocumentid))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (!ids.length) return toast.info("Select at least one document");

    const ok = window.confirm(`Are you sure you want to delete ${ids.length} document(s)?`);
    if (!ok) return;

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/remove`, {
        ids,
        docId,
      });

      if (data?.success === false) throw new Error(data?.message || "Delete failed");

      toast.success(data?.message || "Deleted Successfully");
      fetchDocs();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Error while deleting");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar />

      <div className="p-4">
        <ToastContainer position="top-right" autoClose={2500} />

        {/* Title + Back */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-bold">File Manager Insurance Document</div>

          <button
            className={`${buttonClass} ${hoverClass} text-white px-3 py-2 rounded flex items-center gap-2`}
            onClick={() => navigate(-1)}
            type="button"
          >
            <FaArrowLeft /> Back
          </button>
        </div>

        {/* Remove button */}
        <div className="flex items-center justify-end mb-2">
          <button
            className={`${
              anyChecked ? "bg-red-600 hover:bg-red-700" : "bg-gray-400"
            } text-white px-3 py-2 rounded flex items-center gap-2`}
            onClick={handleRemove}
            type="button"
            disabled={!anyChecked || loading}
            title={!anyChecked ? "Select documents to remove" : "Remove selected"}
          >
            <FaTrash /> Remove
          </button>
        </div>

        {/* Table */}
        <div className="border rounded overflow-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="p-2 border w-[80px]">S.No</th>
                <th className="p-2 border">Document</th>
                <th className="p-2 border w-[120px]">View</th>
                <th className="p-2 border w-[140px]">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      disabled={!rows.length}
                    />
                    <span>Select</span>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td className="p-3 border" colSpan={4}>
                    Loading...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((r, idx) => (
                  <tr key={r.fld_Insurancedocumentid ?? idx} className="hover:bg-gray-50">
                    <td className="p-2 border">{idx + 1}</td>

                    <td className="p-2 border">{r.fld_document || ""}</td>

                    <td className="p-2 border">
                      <button
                        className={`${buttonClass} ${hoverClass} text-white px-2 py-1 rounded flex items-center gap-2`}
                        onClick={() => handleView(r.fld_document)}
                        type="button"
                      >
                        <FaEye /> Show
                      </button>
                    </td>

                    <td className="p-2 border">
                      <input
                        type="checkbox"
                        checked={!!selected[r.fld_Insurancedocumentid]}
                        onChange={() => toggleRow(r.fld_Insurancedocumentid)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="p-3 border text-center" colSpan={4}>
                    No Records Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && !rows.length ? (
          <div className="mt-2 text-sm text-gray-600">No Records Found</div>
        ) : null}
      </div>
    </VehicleLayoutWrapper>
  );
}
